import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiCall } from '../../../services/realApi';
import { useAuth } from '../../../contexts/AuthContext';
import { useClient } from '../../../contexts/ClientContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../utils/permissions';
import { FiLock as Lock, FiUsers as Users, FiShield as Shield } from 'react-icons/fi';
import UsersTab, { UserRecord } from './components/UsersTab';
import RolesTab, { Role } from './components/RolesTab';
import HierarchyTab from './components/HierarchyTab';
import AdminUsersTab from './components/AdminUsersTab';
import SSOTab from './components/SSOTab';

interface TabDefinition {
  id: string;
  label: string;
  count?: number;
  badge?: boolean;
}

const AccessManagementPage = () => {
  const { user } = useAuth();
  const { selectedClientId, accessibleClients, isLoadingClients } = useClient();
  const { hasPermission, isAdmin } = usePermissions();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const isPartner = user?.role === 'partner';
  const isAWS = user?.role === 'aws';

  // Check if user is on assessment_only plan
  const isAssessmentOnly = useMemo(
    () => user?.client?.subscription_plan === 'assessment_only',
    [user?.client?.subscription_plan]
  );

  // Partner and AWS users must send client_id (selected client from ClientSelector)
  const needsClientId = useMemo(
    () => hasPermission(PERMISSIONS.CLIENTS_VIEW) || hasPermission(PERMISSIONS.CLIENTS_MANAGE),
    [hasPermission]
  );

  // Derive selected client name and partner name for partner tab labels
  const selectedClientName = useMemo(() => {
    if (!isPartner) return '';
    const client = accessibleClients.find(c => c.id === selectedClientId);
    return client?.name || 'Client';
  }, [isPartner, accessibleClients, selectedClientId]);

  const [activeTab, setActiveTab] = useState(() => {
    return isPartner ? 'client-users' : 'users';
  });
  const [loading, setLoading] = useState(true);

  // Check if user can view roles (AWS users and system admins only)
  const canViewRoles = useMemo(() => {
    return hasPermission(PERMISSIONS.SYSTEM_ADMIN) || isAdmin;
  }, [isAdmin, hasPermission]);

  // Check if user can view hierarchy (only aivilo_staff)
  const canViewHierarchy = useMemo(() => {
    return hasPermission(PERMISSIONS.SYSTEM_ADMIN) || isAdmin;
  }, [isAdmin, hasPermission]);

  const canViewAdmin = useMemo(() => {
    return hasPermission(PERMISSIONS.SYSTEM_ADMIN) || isAdmin;
  }, [isAdmin, hasPermission]);

  // SSO tab: visible to account_owner, aivilo_staff, and partner admins
  const canViewSSO = useMemo(() => {
    if (hasPermission(PERMISSIONS.SYSTEM_ADMIN) || isAdmin) return true;
    if (user?.client_role === 'account_owner') return true;
    if (user?.role === 'partner') return true;
    return false;
  }, [isAdmin, user?.client_role, user?.role, hasPermission]);

  // Summary data
  const [summaryData, setSummaryData] = useState({
    totalUsers: 0,
    totalClientUsers: 0,
    totalPartnerUsers: 0,
    totalRoles: 0,
    totalHierarchyUsers: 0
  });

  // Tab data
  const [clientUsers, setClientUsers] = useState<UserRecord[]>([]);
  const [partnerUsers, setPartnerUsers] = useState<UserRecord[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);

  // Check if user can see partner users (partners and aivilo_staff)
  const canViewPartnerUsers = useMemo(() => {
    // Partner users can always see their own org's users
    if (isPartner) return true;
    return hasPermission(PERMISSIONS.PARTNERS_VIEW) || hasPermission(PERMISSIONS.PARTNERS_MANAGE);
  }, [isPartner, hasPermission]);

  // Derive partner name from /me response or partner users data
  const partnerName = useMemo(() => {
    if (user?.partner?.name) return user.partner.name;
    if (partnerUsers.length > 0 && partnerUsers[0].organization) {
      return partnerUsers[0].organization;
    }
    return 'Partner';
  }, [user?.partner?.name, partnerUsers]);

  const loadSummaryData = useCallback(async () => {
    try {
      // For partner/AWS, /users requires client_id (selected client). Skip if none selected.
      let clientUsersPromise;
      if (needsClientId && !selectedClientId) {
        clientUsersPromise = Promise.resolve([]);
      } else {
        const clientUsersEndpoint =
          needsClientId && selectedClientId ? `/users?client_id=${selectedClientId}&user_type=client` : '/users?user_type=client';
        clientUsersPromise = apiCall(clientUsersEndpoint).catch(() => []);
      }

      const promises = [clientUsersPromise];

      // Load partner users for partners and aivilo_staff
      if (canViewPartnerUsers) {
        promises.push(apiCall('/users?user_type=partner').catch(() => []));
      }

      // Only load roles data if user can view roles
      if (canViewRoles) {
        promises.push(apiCall('/roles').catch(() => []));
      }

      // Only load hierarchy data if user can view hierarchy (aivilo_staff only)
      if (canViewHierarchy) {
        promises.push(apiCall('/aws-users/hierarchy').catch(() => ({ total_users: 0 })));
      }

      const results = await Promise.all(promises);
      let resultIndex = 0;

      const clientUsersData = results[resultIndex++];
      const partnerUsersData = canViewPartnerUsers ? results[resultIndex++] : [];
      const rolesData = canViewRoles ? results[resultIndex++] : [];
      const hierarchyData = canViewHierarchy ? results[resultIndex++] : null;

      const totalClientUsers = Array.isArray(clientUsersData) ? clientUsersData.length : 0;
      const totalPartnerUsers = Array.isArray(partnerUsersData) ? partnerUsersData.length : 0;
      const totalUsers = totalClientUsers + totalPartnerUsers;
      const totalRoles = canViewRoles && Array.isArray(rolesData) ? rolesData.length : 0;
      const totalHierarchyUsers = hierarchyData?.total_users || 0;

      setSummaryData({
        totalUsers,
        totalClientUsers,
        totalPartnerUsers,
        totalRoles,
        totalHierarchyUsers
      });
    } catch (err) {
      console.error('Error loading summary data:', err);
      // Set defaults on error
      setSummaryData({
        totalUsers: 0,
        totalClientUsers: 0,
        totalPartnerUsers: 0,
        totalRoles: 0,
        totalHierarchyUsers: 0
      });
    } finally {
      setLoading(false);
    }
  }, [canViewRoles, canViewHierarchy, canViewPartnerUsers, needsClientId, selectedClientId]);

  const loadClientUsers = useCallback(async () => {
    try {
      if (needsClientId && !selectedClientId) {
        setClientUsers([]);
        return;
      }
      const endpoint =
        needsClientId && selectedClientId
          ? `/users?client_id=${selectedClientId}&user_type=client`
          : '/users?user_type=client';
      const data = await apiCall(endpoint);
      setClientUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading client users:', err);
      setClientUsers([]);
    }
  }, [needsClientId, selectedClientId]);

  const loadPartnerUsers = useCallback(async () => {
    if (!canViewPartnerUsers) {
      setPartnerUsers([]);
      return;
    }
    try {
      const data = await apiCall('/users?user_type=partner');
      setPartnerUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading partner users:', err);
      setPartnerUsers([]);
    }
  }, [canViewPartnerUsers]);

  const handleStartTrial = () => {
    // Preserve assessment context from URL (if present)
    const assessmentId = searchParams.get('assessment_id');
    const token = searchParams.get('token');

    if (assessmentId && token) {
      // Pass assessment context to trial signup
      navigate(`/hub/trial-info?assessment_id=${assessmentId}&token=${token}`);
    } else {
      // No assessment context, just go to trial info
      navigate('/hub/trial-info');
    }
  };

  const loadRoles = useCallback(async () => {
    if (!canViewRoles) {
      setRoles([]);
      return;
    }
    try {
      const data = await apiCall('/roles');
      setRoles(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading roles:', err);
      setRoles([]);
    }
  }, [canViewRoles]);

  useEffect(() => {
    loadSummaryData();
    loadClientUsers();
    loadPartnerUsers();
    loadRoles();
  }, [loadSummaryData, loadClientUsers, loadPartnerUsers, loadRoles]);

  // Redirect to valid tab if user tries to access a tab they don't have permission for
  useEffect(() => {
    const defaultTab = isPartner ? 'client-users' : 'users';
    if (activeTab === 'roles' && !canViewRoles) {
      setActiveTab(defaultTab);
    }
    if (activeTab === 'hierarchy' && !canViewHierarchy) {
      setActiveTab(defaultTab);
    }
    if (activeTab === 'admin' && !canViewAdmin) {
      setActiveTab(defaultTab);
    }
    if (activeTab === 'sso' && !canViewSSO) {
      setActiveTab(defaultTab);
    }
    if (activeTab === 'aws-sso' && (!canViewSSO || user?.role !== 'aivilo_staff')) {
      setActiveTab(defaultTab);
    }
  }, [activeTab, canViewRoles, canViewHierarchy, canViewAdmin, canViewSSO, isPartner, user?.role]);

  const handleRefresh = () => {
    setLoading(true);
    loadSummaryData();
    loadClientUsers();
    loadPartnerUsers();
    loadRoles();
  };

  // Error handling is done in individual tabs

  // Build tabs based on user role
  const tabs = useMemo<TabDefinition[]>(() => {
    if (isPartner) {
      // Partner users get 4 scoped tabs
      return [
        { id: 'client-users', label: `${selectedClientName} Users`, count: summaryData.totalClientUsers },
        { id: 'partner-users', label: `${partnerName} Users`, count: summaryData.totalPartnerUsers },
        { id: 'client-sso', label: `${selectedClientName} SSO & Provisioning` },
        { id: 'partner-sso', label: `${partnerName} SSO & Provisioning` },
      ];
    }
    // Default tabs for client and aivilo_staff
    return [
      { id: 'users', label: 'Users', count: summaryData.totalUsers },
      ...(canViewRoles ? [{ id: 'roles', label: 'Roles', count: summaryData.totalRoles }] : []),
      ...(canViewHierarchy ? [{ id: 'hierarchy', label: 'Hierarchy', count: summaryData.totalHierarchyUsers }] : []),
      ...(canViewAdmin ? [{ id: 'admin', label: 'Admin' }] : []),
      ...(canViewSSO ? [{ id: 'sso', label: 'SSO & Provisioning' }] : []),
      ...(canViewSSO && user?.role === 'aivilo_staff' ? [{ id: 'aws-sso', label: 'AWS SSO & Provisioning' }] : []),
    ];
  }, [isPartner, selectedClientName, partnerName, summaryData, canViewRoles, canViewHierarchy, canViewAdmin, canViewSSO, user?.role]);

  // Block AWS users from Access Management
  if (isAWS) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <Lock className="w-8 h-8 text-gray-400 dark:text-gray-600" />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white transition-colors">
              Access Management
            </h1>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 max-w-2xl mx-auto border border-gray-200 dark:border-gray-700">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-6">
              <Lock className="w-8 h-8 text-gray-400 dark:text-gray-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Feature Not Available
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Access Management is not available for AWS users. Please contact your organization administrator for access management needs.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show restricted access view for assessment_only users
  if (isAssessmentOnly) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <Lock className="w-8 h-8 text-gray-400 dark:text-gray-600" />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white transition-colors">
              Access Management
            </h1>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 max-w-2xl mx-auto border border-gray-200 dark:border-gray-700">
          <div className="text-center">
            {/* Lock Icon */}
            <div className="mx-auto w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-6">
              <Lock className="w-8 h-8 text-gray-400 dark:text-gray-500" />
            </div>

            {/* Message */}
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Feature Not Available
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Access Management is not included in the assessment-only plan. Upgrade to the full platform trial to manage users, roles, and permissions.
            </p>

            {/* Upgrade CTA */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 mb-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                Included in Full Platform Trial:
              </h3>
              <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2 text-left max-w-md mx-auto">
                <li className="flex items-start">
                  <span className="text-blue-600 dark:text-blue-400 mr-2">•</span>
                  <span>Multi-user access with role-based permissions</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 dark:text-blue-400 mr-2">•</span>
                  <span>Team collaboration features</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 dark:text-blue-400 mr-2">•</span>
                  <span>Cloud cost optimization & FinOps</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 dark:text-blue-400 mr-2">•</span>
                  <span>GenAI lifecycle management</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 dark:text-blue-400 mr-2">•</span>
                  <span>Security & compliance monitoring</span>
                </li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={handleStartTrial}
                className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 transition-colors"
              >
                Start 60-Day Free Trial
              </button>
              <button
                onClick={() => globalThis.location.href = '/hub/assessments'}
                className="px-6 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Back to Assessments
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-2">
          <Lock className="w-8 h-8 text-blue-500" />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white transition-colors">
            Access Management
          </h1>
        </div>
        <p className="text-gray-600 dark:text-gray-400 transition-colors">
          View user access and role information
        </p>
      </div>

      {/* Summary Cards */}
      <div className={`grid gap-6 mb-8 ${canViewRoles ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
        {/* Total Users */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 transition-colors">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-gray-600 dark:text-gray-400" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 transition-colors">
                Total Users
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white transition-colors">
                {loading ? '...' : summaryData.totalUsers}
              </p>
            </div>
          </div>
        </div>

        {/* Total Roles - Only visible to AWS users and system admins */}
        {canViewRoles && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 transition-colors">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center">
                  <Shield className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 transition-colors">
                  Total Roles
                </p>
                <p className="text-2xl font-semibold text-purple-600 dark:text-purple-400 transition-colors">
                  {loading ? '...' : summaryData.totalRoles}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <span>{tab.label}</span>
              {tab.count != null && (
                <span className={`px-2 py-0.5 text-xs rounded-full ${
                  tab.badge && tab.count > 0
                    ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {/* Partner-specific tabs */}
        {activeTab === 'client-users' && isPartner && (
          <UsersTab
            clientUsers={clientUsers}
            partnerUsers={[]}
            loading={loading}
            onRefresh={handleRefresh}
            showSelectClientPrompt={needsClientId && !selectedClientId && !isLoadingClients}
            canViewPartnerUsers={false}
            scope="client"
          />
        )}
        {activeTab === 'partner-users' && isPartner && (
          <UsersTab
            clientUsers={[]}
            partnerUsers={partnerUsers}
            loading={loading}
            onRefresh={handleRefresh}
            canViewPartnerUsers={canViewPartnerUsers}
            scope="partner"
          />
        )}
        {activeTab === 'client-sso' && isPartner && (
          <SSOTab
            key={`client-sso-${selectedClientId}`}
            orgScope={{ type: 'client', id: selectedClientId }}
          />
        )}
        {activeTab === 'partner-sso' && isPartner && (
          <SSOTab key="partner-sso" />
        )}

        {/* Standard tabs for non-partner roles */}
        {activeTab === 'users' && !isPartner && (
          <UsersTab
            clientUsers={clientUsers}
            partnerUsers={partnerUsers}
            loading={loading}
            onRefresh={handleRefresh}
            showSelectClientPrompt={needsClientId && !selectedClientId && !isLoadingClients}
            canViewPartnerUsers={canViewPartnerUsers}
          />
        )}
        {activeTab === 'roles' && canViewRoles && (
          <RolesTab
            roles={roles}
            loading={loading}
            onRefresh={handleRefresh}
          />
        )}
        {activeTab === 'hierarchy' && (
          <HierarchyTab
            onRefresh={handleRefresh}
          />
        )}
        {activeTab === 'admin' && canViewAdmin && (
          <AdminUsersTab onRefresh={handleRefresh} />
        )}
        {activeTab === 'sso' && canViewSSO && !isPartner && (
          <SSOTab />
        )}
        {activeTab === 'aws-sso' && canViewSSO && user?.role === 'aivilo_staff' && (
          <SSOTab orgScope={{ type: 'aws_global' }} />
        )}
      </div>
    </div>
  );
};

export default AccessManagementPage;

